/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ReactNode,
    useState,
    useRef,
    useEffect,
    ReactElement,
    Children,
    isValidElement,
    cloneElement,
} from 'react';
import { cn } from '../../utils';
import debounce from 'lodash/debounce';
import { ArrowRightIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import Spinner from '../Spinner/Spinner';
import Tooltip from '../Tooltip/Tooltip';
import Badge from '../Badge/Badge';

type MenuProps = {
    promptHeight?: { initial?: number; current?: number };
    children?: ReactNode;
    className?: string;
    position?: 'bottom' | 'top';
};

type AiPromptProps = {
    title?: string;
    subTitle?: string;
    icon?: ReactNode;
    className?: string | object;
    disabled?: boolean;
    placeholder?: string;
    countryList?: string[];
    promptText?: string;
    promptCountry?: string;
    textBoxClassName?: string;
    textPersist?: boolean;
    errorMessage?: string;
    attachment?: boolean;
    maxFileSize?: number;
    allowedFileFormats?: string[];
    file?: File | File[] | null;
    children?: ReactNode;
    errorStatus?: boolean;
    onSubmit: (
        promptText: string,
        promptCountry: string,
        file: File[] | File | null,
    ) => void;
    onChange?: (promptText: string) => void;
};

function AiPrompt({
    title,
    subTitle,
    className = '',
    icon = null,
    disabled = false,
    placeholder = '',
    countryList = [],
    promptText = '',
    promptCountry = '',
    file = null,
    textBoxClassName = '',
    errorMessage = '',
    textPersist = false,
    attachment = false,
    maxFileSize = 5,
    allowedFileFormats = ['.pdf'],
    children,
    errorStatus = false,
    onSubmit,
    onChange,
}: AiPromptProps) {
    const [prompt, setPrompt] = useState<{
        text: string;
        country: string;
        error: { status: boolean; message: string };
        file: File | null;
    }>({
        text: '',
        country: countryList[0] || '',
        error: { status: errorStatus, message: errorMessage },
        file: null,
    });

    const [focused, setFocused] = useState(false);
    const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
    const [promptHeight, setPromptHeight] = useState({
        initial: 0,
        current: 0,
    });

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const countryRef = useRef<HTMLSelectElement>(null);
    const selectRef = useRef<HTMLDivElement>(null);
    const boxRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setPrompt(prevPrompt => ({
            ...prevPrompt,
            text: promptText,
            country: promptCountry,
            file: file instanceof File ? file : null,
            error: { status: errorStatus, message: errorMessage },
        }));
        updateHeight();
    }, [promptText, promptCountry, errorStatus, errorMessage]);

    const setErrorStatus = (status: boolean, message: string = '') => {
        setPrompt(prevPrompt => ({
            ...prevPrompt,
            error: {
                status,
                message,
            },
        }));
    };

    const updateHeight = debounce(() => {
        if (inputRef.current && boxRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
            const maxHeight = Math.min(inputRef.current.scrollHeight, 80);
            setPromptHeight(prevState => ({
                initial:
                    prevState.initial === 0 ? maxHeight : prevState.initial,
                current: maxHeight,
            }));
            inputRef.current.style.maxHeight = `${maxHeight}px`;
            boxRef.current.style.height = 'auto';
            inputRef.current.style.overflowY =
                inputRef.current.scrollHeight > 80 ? 'scroll' : 'hidden';
        }
    }, 100);

    const validateFileFormat = (file: File): boolean => {
        if (allowedFileFormats.length === 0) return true;
        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
        return allowedFileFormats.some(
            format =>
                format.toLowerCase() === fileExtension ||
                file.type.startsWith(format.toLowerCase()),
        );
    };

    const handleButtonClick = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.preventDefault();
        const inputValue = inputRef.current?.value || '';
        const selectedOptionValue = countryRef.current?.value || '';
        if (inputValue === '') {
            setErrorStatus(true);
            return;
        }
        onSubmit(inputValue, selectedOptionValue, prompt.file);
        updateHeight();
        setPrompt(prevPrompt => ({
            ...prevPrompt,
            text: textPersist ? prevPrompt.text : '',
            country: textPersist ? prevPrompt.country : countryList[0] || '',
            error: { status: false, message: '' },
            file: textPersist ? prevPrompt.file : null,
        }));
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            handleButtonClick(event as any);
        }
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0] || null;
        setIsAttachmentLoading(true);
        let errorMessage = '';

        if (file) {
            if (file.size > maxFileSize * 1024 * 1024) {
                errorMessage = `File exceeds the maximum size of ${maxFileSize}MB.`;
            } else if (!validateFileFormat(file)) {
                errorMessage = `Invalid file format. Allowed formats: ${allowedFileFormats.join(', ')}`;
            }
        }

        await new Promise(resolve => setTimeout(resolve, 1000));

        if (errorMessage) {
            setPrompt(prevPrompt => ({
                ...prevPrompt,
                error: { status: true, message: errorMessage },
            }));
        } else {
            setPrompt(prevPrompt => ({
                ...prevPrompt,
                file,
                error: { status: false, message: '' },
            }));
        }

        setIsAttachmentLoading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = () => {
        setPrompt(prevPrompt => ({
            ...prevPrompt,
            file: null,
            error: { status: false, message: '' },
        }));
    };

    const truncateFileName = (fileName: string, maxLength: number = 12) => {
        const simplifiedName = fileName.replace(/[^a-zA-Z0-9]/g, ' ').trim();

        if (simplifiedName.length <= maxLength) {
            return simplifiedName;
        }

        return simplifiedName.slice(0, maxLength - 1) + '\u2026';
    };

    const renderFileAttachment = () => {
        if (!prompt.file) {
            return (
                <button
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="attach-file"
                    className="alli-text-gray-700 hover:alli-text-blue-gray-700"
                    disabled={disabled || isAttachmentLoading}>
                    {isAttachmentLoading ? (
                        <Spinner className="alli-h-5 alli-w-5" />
                    ) : (
                        <Tooltip
                            content="Attach brief (.pdfs only)"
                            data-testid="attach-file"
                            position="top"
                            fontSize="sm">
                            <PaperClipIcon className="alli-h-5 alli-w-5 alli--rotate-45" />
                        </Tooltip>
                    )}
                </button>
            );
        }

        return (
            <Badge
                onClick={handleRemoveFile}
                variant="blue"
                className="alli-max-w-[120px] alli-overflow-hidden alli-text-ellipsis alli-whitespace-nowrap">
                <Tooltip
                    content={prompt.file.name}
                    position="top"
                    fontSize="sm">
                    {truncateFileName(prompt.file.name)}
                </Tooltip>
            </Badge>
        );
    };

    return (
        <div className={cn('alli-block md:alli-w-[40rem]', className)}>
            <div
                className={cn(
                    'alli-flex alli-flex-col alli-items-center alli-justify-center',
                    {
                        'alli-gap-[41px]': title || subTitle,
                    },
                )}>
                <div className="alli-inline-flex alli-flex-col alli-items-center alli-justify-center alli-gap-3">
                    <div className="alli-inline-flex alli-flex-col alli-items-center alli-justify-center alli-gap-2">
                        {icon && (
                            <div className="alli-h-6 alli-w-6 alli-text-gray-700">
                                {icon}
                            </div>
                        )}
                        {title && (
                            <div className="alli-text-xl alli-font-semibold alli-leading-7 alli-text-blue-gray-700">
                                {title}
                            </div>
                        )}
                    </div>
                    {subTitle && (
                        <div className="alli-gap-2.5">
                            <div className="alli-text-sm alli-font-normal alli-leading-tight alli-text-gray-750">
                                {subTitle}
                            </div>
                        </div>
                    )}
                </div>
                <div className="alli-relative alli-w-full">
                    <div className="alli-flex alli-w-full alli-justify-center alli-gap-2">
                        <div className="alli-relative alli-w-full alli-min-w-28 alli-shrink alli-grow alli-self-stretch">
                            <div
                                ref={boxRef}
                                className={cn(
                                    'alli-relative alli-z-20 alli-flex alli-w-full alli-min-w-28 alli-shrink alli-grow alli-self-stretch alli-rounded-3xl alli-border alli-border-blue-gray-200 alli-bg-white alli-px-3 alli-py-3 alli-text-blue-gray-700 sm:alli-text-sm sm:alli-leading-6',
                                    {
                                        'alli-border-blue-600 alli-shadow':
                                            focused,
                                        'alli-border-red-400':
                                            prompt.error.status,
                                        'alli-shadow-none': !focused,
                                        'alli-border alli-border-blue-gray-200':
                                            disabled,
                                    },
                                    textBoxClassName,
                                )}>
                                <textarea
                                    ref={inputRef}
                                    className={cn(
                                        'alli-transition-height alli-w-full alli-resize-none alli-rounded-3xl alli-border-none alli-py-0 alli-text-sm alli-placeholder-gray-700 alli-outline-none alli-duration-300 alli-ease-in-out focus:alli-outline-none focus:alli-ring-0 focus-visible:alli-border-none focus-visible:alli-outline-none focus-visible:alli-ring-0',
                                        {
                                            'alli-cursor-not-allowed alli-text-gray-700 alli-placeholder-gray-600':
                                                disabled,
                                            'alli-cursor-pointer alli-placeholder-gray-700':
                                                !disabled,
                                        },
                                    )}
                                    placeholder={placeholder}
                                    value={prompt.text}
                                    onFocus={() => {
                                        setFocused(true);
                                        setErrorStatus(false);
                                    }}
                                    onBlur={() => {
                                        setFocused(false);
                                        setErrorStatus(false);
                                    }}
                                    rows={1}
                                    disabled={disabled}
                                    onChange={e => {
                                        setPrompt({
                                            ...prompt,
                                            text: e.target.value,
                                            error: {
                                                ...prompt.error,
                                                status: false,
                                            },
                                        });
                                        onChange?.(e.target.value);
                                    }}
                                    onKeyUp={updateHeight}
                                    onKeyDown={e => handleKeyDown(e as any)}
                                />
                                {attachment && (
                                    <div className="alli-mr-2 alli-h-5 alli-max-w-60 alli-items-center">
                                        <input
                                            type="file"
                                            data-testid="file-input"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            className="alli-hidden"
                                            disabled={
                                                disabled || isAttachmentLoading
                                            }
                                            accept={allowedFileFormats.join(
                                                ',',
                                            )}
                                        />
                                        {renderFileAttachment()}
                                    </div>
                                )}
                                {Children.map(children, child => {
                                    if (
                                        isValidElement(child) &&
                                        child.type !== Menu
                                    ) {
                                        return child;
                                    }
                                    return null;
                                })}
                                {countryList.length > 0 && (
                                    <div
                                        ref={selectRef}
                                        className="alli-right-2 alli-top-1 alli-flex alli-h-5 alli-cursor-pointer alli-rounded-r-full">
                                        <span
                                            className="alli-w-px alli-bg-gray-400"
                                            aria-hidden="true"></span>
                                        <select
                                            ref={countryRef}
                                            value={prompt.country}
                                            onChange={e =>
                                                setPrompt({
                                                    ...prompt,
                                                    country: e.target.value,
                                                    error: {
                                                        ...prompt.error,
                                                        status: false,
                                                    },
                                                })
                                            }
                                            onFocus={() => {
                                                setFocused(true);
                                                setErrorStatus(false);
                                            }}
                                            onBlur={() => {
                                                setFocused(false);
                                                setErrorStatus(false);
                                            }}
                                            disabled={disabled}
                                            className={cn(
                                                'alli-rounded-3xl alli-border-none alli-py-0 alli-pl-3 alli-pr-8 alli-text-sm alli-text-black focus:alli-border-none focus:alli-outline-none focus:alli-ring-0 focus-visible:alli-border-none disabled:alli-opacity-100',
                                                {
                                                    'alli-cursor-not-allowed':
                                                        disabled,
                                                    'alli-cursor-pointer':
                                                        !disabled,
                                                },
                                            )}>
                                            {countryList.map(
                                                (country, index) => (
                                                    <option
                                                        key={index}
                                                        value={country}>
                                                        {country}
                                                    </option>
                                                ),
                                            )}
                                        </select>
                                    </div>
                                )}
                            </div>
                            {Children.map(children, child => {
                                if (
                                    isValidElement(child) &&
                                    child.type === Menu
                                ) {
                                    return cloneElement(
                                        child as ReactElement<any>,
                                        {
                                            promptHeight,
                                        },
                                    );
                                }
                                return null;
                            })}
                        </div>
                        <div className="alli-mt-1.5 alli-flex alli-items-start alli-justify-end">
                            <button
                                className={cn(
                                    'alli-inline-flex alli-h-9 alli-w-9 alli-items-center alli-rounded-full alli-p-2.5 alli-text-white alli-shadow-sm',
                                    {
                                        'alli-cursor-not-allowed alli-bg-gray-600 hover:alli-bg-gray-600 focus-visible:alli-outline-none active:alli-bg-gray-600':
                                            disabled,
                                        'alli-cursor-pointer alli-bg-blue-600 hover:alli-bg-blue-500 focus-visible:alli-outline focus-visible:alli-outline-2 focus-visible:alli-outline-offset-2 focus-visible:alli-outline-blue-600 active:alli-bg-blue-700':
                                            !disabled,
                                    },
                                )}
                                type="button"
                                onClick={handleButtonClick}
                                disabled={disabled}
                                data-testid="AiPromptSubmit">
                                <ArrowRightIcon className="alli-h-5 alli-w-5 " />
                            </button>
                        </div>
                        {prompt.error?.status && prompt.error?.message && (
                            <div className="alli-absolute alli-bottom-0 alli-left-0 alli-top-full alli--mb-6 alli-w-full">
                                <p className="alli-whitespace-normal alli-break-words alli-px-2 alli-py-1 alli-text-xs alli-text-red-400">
                                    {prompt.error?.message}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const Menu = ({
    promptHeight,
    className = '',
    children,
    position = 'bottom',
}: MenuProps) => {
    const style =
        position === 'bottom'
            ? {
                  top: promptHeight?.current + 'px',
                  paddingTop: promptHeight?.initial + 'px',
              }
            : {
                  bottom: promptHeight?.current + 'px',
                  paddingBottom: promptHeight?.initial + 'px',
              };
    const roundedClass =
        position === 'bottom' ? 'alli-rounded-b-3xl' : 'alli-rounded-t-3xl';
    return (
        <div
            data-testid="menu"
            className={cn(
                'alli-absolute alli-z-10 alli-h-auto alli-w-full alli-border alli-border-blue-gray-200 alli-bg-white alli-bg-white alli-shadow-md',
                className,
                roundedClass,
            )}
            style={style}>
            {children}
        </div>
    );
};

AiPrompt.Menu = Menu;

export default AiPrompt;
